import { useEffect, useState } from "react";
import { PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader, GlassCard, Loader, Empty } from "../components/kit";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DEFAULT_TIMES = [["08:00", "08:45"], ["08:45", "09:30"], ["09:45", "10:30"], ["10:30", "11:15"], ["11:30", "12:15"], ["12:15", "13:00"]];
const HUES = ["from-blue-500/20", "from-orange-500/20", "from-emerald-500/20", "from-rose-500/20", "from-amber-500/20", "from-sky-500/20", "from-fuchsia-500/15"];

function buildGrid(entries) {
  const g = {};
  entries.forEach((e) => { g[`${e.day}-${e.period}`] = e; });
  return g;
}

export default function Timetable() {
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user.role === "admin";
  const family = ["parent", "student"].includes(user.role);
  const { data: classes } = useApi("/classes");
  const [cls, setCls] = useState("");
  useEffect(() => {
    if (classes?.length && !cls) setCls(classes[0].id);
  }, [classes, cls]);
  const { data, loading, reload } = useApi(cls || family ? `/timetable?class_id=${cls}` : null);
  const [edit, setEdit] = useState(false);
  const [grid, setGrid] = useState({});
  const [times, setTimes] = useState(DEFAULT_TIMES);
  useEffect(() => {
    if (!data) return;
    setGrid(buildGrid(data.entries));
    const tm = DEFAULT_TIMES.map((d, i) => {
      const e = data.entries.find((x) => x.period === i + 1);
      return e ? [e.start, e.end] : d;
    });
    setTimes(tm);
  }, [data]);
  const subjects = [...new Set(Object.values(grid).map((e) => e.subject))];
  const hue = (s) => HUES[subjects.indexOf(s) % HUES.length];
  const setCell = (day, p, k, v) => {
    const key = `${day}-${p}`;
    setGrid({ ...grid, [key]: { day, period: p, start: times[p - 1][0], end: times[p - 1][1], subject: "", teacher_name: "", ...grid[key], [k]: v } });
  };
  const save = async () => {
    const entries = Object.values(grid).map((e) => ({ ...e, start: times[e.period - 1][0], end: times[e.period - 1][1] }));
    try {
      await api.put(`/timetable/${cls}`, { entries });
      toast.success("Timetable saved");
      setEdit(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const clsName = classes?.find((c) => c.id === (data?.class_id || cls));
  return (
    <div>
      <PageHeader eyebrow={clsName ? `${clsName.name} ${clsName.section}` : "Weekly"} title={t("Timetable")}
        actions={<>
          {!family && (
            <select className="field !w-48 !rounded-full" value={cls} onChange={(e) => { setCls(e.target.value); setEdit(false); }} data-testid="timetable-class-select">
              {classes?.map((c) => <option key={c.id} value={c.id}>{c.name} {c.section}</option>)}
            </select>
          )}
          {isAdmin && !edit && <button className="btn-primary" onClick={() => setEdit(true)} data-testid="timetable-edit-btn"><PencilSimple size={16} /> {t("Edit")}</button>}
          {isAdmin && edit && <><button className="btn-ghost" onClick={() => { setEdit(false); setGrid(buildGrid(data.entries)); }} data-testid="timetable-cancel-btn"><X size={16} /> {t("Cancel")}</button>
            <button className="btn-primary" onClick={save} data-testid="timetable-save-btn"><FloppyDisk size={16} /> {t("Save")}</button></>}
        </>} />
      <GlassCard className="p-4 sm:p-6">
        {loading || !data ? <Loader /> : !data.entries.length && !edit ? <Empty hint={isAdmin ? "Click Edit to build this class's weekly timetable." : "Timetable not published yet."} /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-separate border-spacing-2" data-testid="timetable-grid">
              <thead>
                <tr>
                  <th className="eyebrow text-left w-28">{t("Period")}</th>
                  {DAYS.map((d) => <th key={d} className="eyebrow text-left">{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {times.map(([a, b], i) => (
                  <tr key={i}>
                    <td className="align-top">
                      <div className="font-display text-lg text-white">P{i + 1}</div>
                      {edit ? (
                        <div className="mt-1 space-y-1">
                          <input className="field !py-1 !px-2 text-xs" value={a} onChange={(e) => setTimes(times.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} data-testid={`tt-start-${i + 1}`} />
                          <input className="field !py-1 !px-2 text-xs" value={b} onChange={(e) => setTimes(times.map((x, j) => (j === i ? [x[0], e.target.value] : x)))} data-testid={`tt-end-${i + 1}`} />
                        </div>
                      ) : <div className="text-xs text-zinc-500">{a}–{b}</div>}
                    </td>
                    {DAYS.map((d) => {
                      const e = grid[`${d}-${i + 1}`];
                      return (
                        <td key={d} className="align-top">
                          {edit ? (
                            <div className="space-y-1">
                              <input className="field !py-1.5 !px-2 text-xs" placeholder={t("Subject")} value={e?.subject || ""} onChange={(ev) => setCell(d, i + 1, "subject", ev.target.value)} data-testid={`tt-${d}-${i + 1}-subject`} />
                              <input className="field !py-1.5 !px-2 text-xs" placeholder={t("Teacher")} value={e?.teacher_name || ""} onChange={(ev) => setCell(d, i + 1, "teacher_name", ev.target.value)} data-testid={`tt-${d}-${i + 1}-teacher`} />
                            </div>
                          ) : e?.subject ? (
                            <div className={`glass-hover rounded-2xl border border-white/10 bg-gradient-to-br ${hue(e.subject)} to-transparent p-3 min-h-[72px]`} data-testid={`tt-cell-${d}-${i + 1}`}>
                              <div className="text-sm font-semibold text-white">{e.subject}</div>
                              <div className="mt-1 text-[11px] text-zinc-400 truncate">{e.teacher_name}</div>
                            </div>
                          ) : <div className="rounded-2xl border border-dashed border-white/5 min-h-[72px]" />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
